import { useState, useEffect } from "react";
import SectionTitle from "../components/SectionTitle";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import faqLottie from "../assets/lottie-files/faq.lottie";
import { sanityFetch } from "../sanityClient";

type FAQ = {
  question: string;
  answer: string;
  tags: string[];
  link?: string;
};

const allTags = ["All", "Main Market Hall", "Kitchen", "Workshop Room"];

const FAQs: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [faqs, setFaqs] = useState<FAQ[]>([]);

  useEffect(() => {
    sanityFetch<FAQ[]>(`*[_type == "faq"] | order(order asc) { question, answer, tags, link }`)
      .then(setFaqs);
  }, []);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const filteredFaqs =
    selectedTag === "All"
      ? faqs
      : faqs.filter((faq) =>
          faq.tags.some(
            (tag) => tag.toLowerCase() === selectedTag.toLowerCase()
          )
        );

  return (
    <div>
      {/* Hero */}
      <div
        style={{
          background:
            "linear-gradient(to bottom right, #D46540 65%, rgb(240, 240, 240) 100%)",
          padding: "4rem 1rem",
          color: "white",
        }}
      >
        <div style={styles.contentWrapper}>
          <div style={styles.textWrapper}>
            <SectionTitle
              parts={[
                { text: "❓ Frequently Asked Questions", color: "white" },
              ]}
              align="left"
            />
            <p style={{ fontSize: "1.25rem", lineHeight: "1.6" }}>
              Got questions about the Market? We've got answers! Browse below to
              find everything you need to know.
            </p>
          </div>

          <div style={styles.lottieWrapper}>
            <DotLottieReact
              src={faqLottie}
              loop
              autoplay
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div style={{ background: "#f0f4f4", padding: "1.5rem 1rem", borderBottom: "1px solid #e0e0e0" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", display: "flex", flexWrap: "wrap" as const, gap: "0.625rem", justifyContent: "center" }}>
          {allTags.map((tag) => {
            const isActive = selectedTag === tag;
            const accentColor =
              tag === "Main Market Hall" ? "#157D77"
              : tag === "Workshop Room" ? "#F57C00"
              : tag === "Kitchen" ? "#12646C"
              : "#D46540";
            return (
              <button
                key={tag}
                onClick={() => { setSelectedTag(tag); setOpenIndex(null); }}
                style={{
                  padding: "0.5rem 1.25rem",
                  background: isActive ? accentColor : "white",
                  color: isActive ? "white" : accentColor,
                  border: `2px solid ${accentColor}`,
                  borderRadius: "999px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  letterSpacing: "0.02em",
                  transition: "all 0.2s ease",
                  boxShadow: isActive ? `0 2px 8px ${accentColor}44` : "none",
                }}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      {/* FAQ List */}
      <div style={{ background: "#f8fafa", padding: "2.5rem 1rem", color: "#333" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          {filteredFaqs.length === 0 ? (
            <p style={{ textAlign: "center", fontSize: "1.1rem", color: "#666" }}>
              No FAQs found for "{selectedTag}".
            </p>
          ) : (
            filteredFaqs.map((item, index) => {
              const isOpen = openIndex === index;
              const tagColor =
                item.tags[0] === "Main Market Hall" ? "#157D77"
                : item.tags[0] === "Workshop Room" ? "#F57C00"
                : item.tags[0] === "Kitchen" ? "#12646C"
                : "#D46540";
              return (
                <div
                  key={index}
                  onClick={() => toggleQuestion(index)}
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    marginBottom: "0.875rem",
                    boxShadow: isOpen
                      ? "0 4px 16px rgba(0,0,0,0.08)"
                      : "0 1px 4px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                    border: `1px solid ${isOpen ? tagColor + "44" : "#eee"}`,
                    borderLeft: `4px solid ${tagColor}`,
                    cursor: "pointer",
                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                    userSelect: isOpen ? "text" : "none",
                  }}
                >
                  <div style={{ padding: "1.1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                    <div style={{ display: "flex", flexDirection: "column" as const, gap: "0.35rem", flex: 1 }}>
                      {item.tags.length > 0 && (
                        <span style={{
                          color: tagColor,
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "uppercase" as const,
                          letterSpacing: "0.06em",
                        }}>
                          {item.tags[0]}
                        </span>
                      )}
                      <span style={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.4 }}>
                        {item.question}
                      </span>
                    </div>
                    <svg
                      width="20" height="20" viewBox="0 0 20 20" fill="none"
                      style={{ flexShrink: 0, transition: "transform 0.25s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", color: tagColor }}
                    >
                      <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {isOpen && (
                    <div style={{ padding: "0 1.25rem 1.25rem", borderTop: `1px solid ${tagColor}22` }}>
                      <p style={{ margin: "0.75rem 0 0", fontSize: "0.975rem", lineHeight: 1.7, color: "#444", whiteSpace: "pre-line" }}>
                        {item.answer}
                        {item.link && (
                          <>
                            {" "}
                            <a href={item.link} style={{ color: tagColor, fontWeight: 600, textDecoration: "underline" }}>
                              Learn more
                            </a>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};


const styles = {
  contentWrapper: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "2rem",
  } as React.CSSProperties,
  textWrapper: {
    flex: "1 1 500px",
    minWidth: "300px",
  } as React.CSSProperties,
  lottieWrapper: {
    flex: "1 1 400px",
    minWidth: "300px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  } as React.CSSProperties,
};

export default FAQs;
